import { useEffect } from 'react';
import { Capacitor, registerPlugin } from '@capacitor/core';

import { backStackEmpty, subscribeBackStack } from './backButton';
import { isStoreBuild } from './platform';

/**
 * Свайп назад от левого края на iPhone (24.09.2026, сборка 61).
 *
 * Жест даёт сам WKWebView (`ios/App/App/NativeNavPlugin.swift`), а здесь решается, КОГДА он включён. Правило
 * повторяет аппаратную «Назад» на Android (`hooks/useAndroidBack.ts`), только без окна «выйти?»:
 *   - вне кабинета — выключен, КРОМЕ экранов из `OUTSIDE_SWIPE`, на которые пришли переходом (02.10.2026):
 *     свои страницы документов и «Забыли пароль?», регистрация, вход по коду. Там есть куда вернуться и есть
 *     кнопка «Назад» — жест был выключен без причины. Сам вход, ввод кода, подтверждение почты и экран Face ID —
 *     без жеста: назад там некуда или шаг вернул бы на пройденный этап. Открытый первым экран (история пуста) —
 *     тоже без жеста;
 *   - корень раздела меню («Главная», «Обучение», «Словарь»…) — выключен: в приложениях iOS на вкладке жеста
 *     нет, а у нас история линейная, и свайп увёл бы на соседнюю вкладку;
 *   - открыто окно, лист или вопрос перед выходом из теста (стек `lib/backButton.ts` не пуст) — выключен:
 *     системный жест обработчиков не спрашивает и обошёл бы их молча;
 *   - всё остальное — включён.
 *
 * ⚠️ ТОЛЬКО В ПРИЛОЖЕНИИ И ТОЛЬКО НА iOS. На Android «назад» — системный жест, он уже идёт через
 * `useAndroidBack`. В браузере ничего не вызывается. Сторож — `src/app/nativeOnlyInBrowser.test.ts`.
 */

interface NativeNavPlugin {
  setSwipeBack(options: { enabled: boolean }): Promise<void>;
}

const NativeNav = registerPlugin<NativeNavPlugin>('NativeNav');

function trimSlash(path: string): string {
  return path.replace(/\/+$/, '') || '/';
}

/**
 * Экраны вне кабинета, где свайп назад есть, если на них пришли переходом. Внешние документы (Google Docs на iOS)
 * открываются системным окном — к ним жест отношения не имеет; здесь только СВОИ экраны.
 */
export const OUTSIDE_SWIPE: readonly string[] = [
  '/privacy',
  '/terms',
  '/support',
  '/delete-account',
  '/forgot-password',
  '/register',
  '/login-code',
];

/**
 * Можно ли свайп назад на этом экране. `roots === null` — экран вне кабинета.
 * `canGoBack` — в истории приложения есть предыдущая запись (`history.state.idx > 0`): нужен только вне кабинета,
 * внутри него на экран, не являющийся корнем, всегда приходят переходом.
 */
export function swipeBackAllowed(
  path: string,
  roots: readonly string[] | null,
  stackEmpty: boolean,
  canGoBack = false,
): boolean {
  if (!stackEmpty) return false;
  const here = trimSlash(path);
  if (!roots) return canGoBack && OUTSIDE_SWIPE.includes(here);
  return !roots.some((r) => trimSlash(r) === here);
}

/** Есть ли в истории приложения куда вернуться. `history.length` не годится: в нём и записи «вперёд». */
function historyHasBack(): boolean {
  return ((window.history.state as { idx?: number } | null)?.idx ?? 0) > 0;
}

type RouterLike = {
  state: { location: { pathname: string } };
  subscribe: (fn: (state: { location: { pathname: string } }) => void) => unknown;
};

let roots: readonly string[] | null = null;
let path = '/';
let applied: boolean | null = null;
let active = false;

function iosApp(): boolean {
  return isStoreBuild() && Capacitor.getPlatform() === 'ios';
}

let pending = false;

/**
 * Изменения за один такт склеиваются: при смене экрана кабинет на миг снимает и снова ставит корни, и без
 * склейки в натив уходило «вкл → выкл → вкл» за 40 мс (видно в журнале пробы 24.09). Уходит итог.
 */
function sync(): void {
  if (!active || pending) return;
  pending = true;
  queueMicrotask(apply);
}

function apply(): void {
  pending = false;
  const on = swipeBackAllowed(path, roots, backStackEmpty(), historyHasBack());
  if (on === applied) return;
  applied = on;
  void NativeNav.setSwipeBack({ enabled: on }).catch(() => {
    // Нет плагина (старая сборка) — жеста просто не будет. Запомненное сбрасываем, чтобы попробовать снова.
    applied = null;
  });
}

export function initNativeNav(router: RouterLike): void {
  if (!iosApp()) return;
  active = true;
  path = router.state.location.pathname;
  router.subscribe((state) => {
    path = state.location.pathname;
    sync();
  });
  subscribeBackStack(sync);
  sync();
}

// ─── Переходы между экранами, как в iOS ─────────────────────────────────────────────────────────────────
//
// Шаг вглубь — новый экран въезжает справа, прежний уходит влево; шаг назад кнопкой — наоборот. Рисует это
// View Transitions (WKWebView с iOS 18, Chrome WebView 111+; на старых — мгновенно, без поломки), CSS — в
// index.css под `html.native[data-nav-dir]`.
//
// ⚠️ МЕТКА НАПРАВЛЕНИЯ СТАВИТСЯ ТОЛЬКО ИЗ КОДА. Шаг назад СИСТЕМНЫМ ЖЕСТОМ iOS приходит в React Router без неё
// (это обычный POP), и CSS анимацию не играет: жест уже сдвинул страницу сам, второй проезд был бы двойным.
// Router при этом всё равно зовёт startViewTransition (он повторяет переход, которым сюда пришли), — без метки
// это мгновенная смена кадра.
// ⚠️ ВКЛАДКИ — БЕЗ АНИМАЦИИ, как в iOS: переход на корень раздела не едет. Замена адреса (redirect) — тоже.

export const NAV_DIR_ATTR = 'data-nav-dir';
let dirTimer: ReturnType<typeof setTimeout> | undefined;

function markDir(dir: 'forward' | 'back'): void {
  const html = document.documentElement;
  html.setAttribute(NAV_DIR_ATTR, dir);
  clearTimeout(dirTimer);
  // Переход длится 350 мс; метка живёт чуть дольше и снимается, чтобы следующий POP (жест) её не унаследовал.
  dirTimer = setTimeout(() => html.removeAttribute(NAV_DIR_ATTR), 600);
}

type To = string | { pathname?: string } | number;
type NavOpts = { replace?: boolean; viewTransition?: boolean } & Record<string, unknown>;

/** Куда анимировать переход: `null` — без анимации. Чистая функция — ради проверки. */
export function transitionFor(
  to: To,
  opts: NavOpts | undefined,
  currentPath: string,
  tabRoots: readonly string[] | null,
): 'forward' | 'back' | null {
  if (typeof to === 'number') return to < 0 ? 'back' : null;
  if (opts?.replace) return null;
  let target: string;
  if (typeof to === 'string') target = new URL(to, `https://x${currentPath.endsWith('/') ? currentPath : `${currentPath}/`}`).pathname;
  else target = to.pathname ?? currentPath;
  if (trimSlash(target) === trimSlash(currentPath)) return null;
  if (tabRoots?.some((r) => trimSlash(r) === trimSlash(target))) return null;
  return 'forward';
}

type NavigableRouter = RouterLike & { navigate: (to: never, opts?: never) => unknown };

export function installNativeTransitions(router: NavigableRouter): void {
  if (!isStoreBuild()) return;
  const original = router.navigate.bind(router) as (to: To, opts?: NavOpts) => unknown;
  (router as unknown as { navigate: (to: To, opts?: NavOpts) => unknown }).navigate = (to, opts) => {
    const dir = transitionFor(to, opts, router.state.location.pathname, roots);
    if (!dir) return original(to, opts);
    markDir(dir);
    // `navigate(-1)` опций не принимает — обратный переход Router повторит сам (он помнит прямой).
    return typeof to === 'number' ? original(to, opts) : original(to, { ...opts, viewTransition: true });
  };
}

/**
 * Кабинет сообщает свои корни разделов — те же, что получает `useAndroidBack`. Пока кабинет не на экране,
 * корней нет, и жест выключен.
 */
export function useSwipeBackRoots(paths: readonly string[]): void {
  useEffect(() => {
    if (!isStoreBuild()) return;
    roots = paths;
    sync();
    return () => {
      roots = null;
      sync();
    };
  }, [paths]);
}
